/**
 * ============================================================
 * Mining Survey WebGIS
 * Measurement Module
 *
 * Features:
 * - Distance
 * - Area
 * - Perimeter
 *
 * Calculation:
 * - WGS84 Lat/Lon -> UTM Zone 50N
 * - Result in meter / square meter
 * ============================================================
 */

import L from "leaflet";

import AppState from "../../core/app.js";


class Measurement {

    constructor() {

        this.map = null;

        this.mode = null;

        this.points = [];

        this.markers = [];

        this.line = null;

        this.polygon = null;

        this.segmentLabels = [];

        this.resultPanel = null;

        this.panel = null;

        this.active = false;

        this.lastMarkerClick = null;
    }


    /* ========================================================
       INIT
       ======================================================== */

    init() {

        this.map = AppState.map;

        if (!this.map) {

            console.error(
                "[Measurement] Map belum tersedia."
            );

            return;
        }


        this.panel =
            document.getElementById(
                "measurementPanel"
            );

        this.resultPanel =
            document.getElementById(
                "measurementResult"
            );


        this.bindUI();


        console.log(
            "[Measurement] Ready"
        );
    }


    /* ========================================================
       UI
       ======================================================== */

    bindUI() {

        document
            .getElementById("btnMeasure")
            ?.addEventListener(
                "click",
                () => {

                    this.open();

                    this.closeToolbar();
                }
            );


        document
            .getElementById("btnCloseMeasurement")
            ?.addEventListener(
                "click",
                () => {

                    this.close();
                }
            );


        document
            .getElementById("btnMeasureDistance")
            ?.addEventListener(
                "click",
                () => {

                    this.start("distance");
                }
            );


        document
            .getElementById("btnMeasureArea")
            ?.addEventListener(
                "click",
                () => {

                    this.start("area");
                }
            );


        document
            .getElementById("btnMeasurementClear")
            ?.addEventListener(
                "click",
                () => {

                    this.clear();
                }
            );


        document
            .getElementById("btnMeasurementFinish")
            ?.addEventListener(
                "click",
                () => {

                    this.finish();
                }
            );


        this.map.on(
            "click",
            (e) => {

                if (!this.active) {
                    return;
                }

                // Jika click berasal dari marker,
                // jangan tambah point baru
                if (
                    e.originalEvent &&
                    e.originalEvent.target &&
                    e.originalEvent.target.closest(".measurement-point")
                ) {
                    return;
                }

                this.addPoint(e.latlng);
            }
        );
    }


    /* ========================================================
       OPEN / CLOSE
       ======================================================== */

    open() {

        this.panel?.classList.add(
            "show"
        );
    }


    close() {

        this.stopMode();

        this.clear();

        AppState.measurementActive = false;

        this.panel?.classList.remove(
            "show"
        );
    }


    closeToolbar() {

        const toolbar =
            document.getElementById(
                "toolbar"
            );

        if (
            window.innerWidth <= 768
        ) {

            toolbar?.classList.remove(
                "show"
            );
        }
    }


    /* ========================================================
       START MODE
       ======================================================== */

    start(mode) {

        if (
            mode !== "distance" &&
            mode !== "area"
        ) {
            return;
        }


        this.clear();


        this.mode = mode;

        this.active = true;

        AppState.measurementActive = true;

        this.updateModeButtons();


        if (mode === "distance") {

            this.setStatus(
                "Distance: klik titik pada peta"
            );

        }


        if (mode === "area") {

            this.setStatus(
                "Area: klik minimal 3 titik"
            );
        }


        console.log(
            `[Measurement] Mode: ${mode}`
        );
    }


    /* ========================================================
       STOP MODE
       ======================================================== */

    stopMode() {

        this.active = false;

        this.mode = null;

        this.updateModeButtons();
    }


 /* ========================================================
   ADD POINT
   ======================================================== */
    addPoint(latlng) {

        this.points.push(latlng);

        const marker =
            L.marker(
                latlng,
                {
                    draggable: true,

                    icon: L.divIcon({
                        className: "measurement-point",

                        html: `
                            <div class="measurement-point-dot"></div>
                        `,

                        iconSize: [14, 14],

                        iconAnchor: [7, 7]
                    })
                }
            ).addTo(this.map);


        this.markers.push(marker);


        /* ====================================================
        STOP CLICK FROM REACHING MAP
        ==================================================== */

        marker.on(
            "click",
            (e) => {

                L.DomEvent.stopPropagation(e);
            }
        );


        /* ====================================================
        DRAG POINT
        ==================================================== */

        marker.on(
            "drag",
            (e) => {

                const index =
                    this.markers.indexOf(marker);

                if (index === -1) {
                    return;
                }


                this.points[index] =
                    e.target.getLatLng();


                if (
                    this.mode === "distance"
                ) {

                    this.updateDistance();
                }


                if (
                    this.mode === "area"
                ) {

                    this.updateArea();
                }
            }
        );


        /* ====================================================
        DOUBLE CLICK = DELETE POINT
        ==================================================== */

        marker.on(
            "dblclick",
            (e) => {

                L.DomEvent.stopPropagation(e);

                L.DomEvent.preventDefault(e);


                const index =
                    this.markers.indexOf(marker);


                if (index === -1) {
                    return;
                }


                // Hapus marker
                marker.remove();


                // Hapus data point
                this.markers.splice(
                    index,
                    1
                );

                this.points.splice(
                    index,
                    1
                );


                // Update measurement
                if (
                    this.mode === "distance"
                ) {

                    this.updateDistance();
                }


                if (
                    this.mode === "area"
                ) {

                    this.updateArea();
                }


                console.log(
                    `[Measurement] Point deleted: ${index + 1}`
                );
            }
        );


        /* ====================================================
        INITIAL UPDATE
        ==================================================== */

        if (
            this.mode === "distance"
        ) {

            this.updateDistance();
        }


        if (
            this.mode === "area"
        ) {

            this.updateArea();
        }
    }


    /* ========================================================
       DISTANCE
       ======================================================== */

    updateDistance() {

        if (
            this.points.length < 2
        ) {

            this.setStatus(
                `Distance: ${this.points.length} point`
            );

            return;
        }


        const latlngs =
            this.points.map(
                p => [p.lat, p.lng]
            );


        if (!this.line) {

            this.line =
                L.polyline(
                    latlngs,
                    {
                        color: "#1565c0",

                        weight: 4,

                        interactive: false
                    }
                ).addTo(this.map);

        } else {

            this.line.setLatLngs(
                latlngs
            );
        }


        let total = 0;

        const segments = [];


        for (
            let i = 1;
            i < this.points.length;
            i++
        ) {

            const a =
                this.latLngToUTM(
                    this.points[i - 1]
                );

            const b =
                this.latLngToUTM(
                    this.points[i]
                );


            const dx =
                b.easting -
                a.easting;

            const dy =
                b.northing -
                a.northing;


            const distance =
                Math.sqrt(
                    dx * dx +
                    dy * dy
                );


            total += distance;

            segments.push(
                distance
            );
        }


        this.updateDistanceLabels(
            segments
        );


        this.resultPanel.innerHTML = `

            <div class="measurement-title">
                Distance
            </div>

            <div class="measurement-total">
                ${this.formatDistance(total)}
            </div>

            <div class="measurement-detail">
                ${segments.length} segment
                · ${this.points.length} points
            </div>

        `;
    }


    /* ========================================================
       DISTANCE LABELS
       ======================================================== */

    updateDistanceLabels(
        segments
    ) {

        this.clearLabels();


        for (
            let i = 1;
            i < this.points.length;
            i++
        ) {

            const a =
                this.points[i - 1];

            const b =
                this.points[i];


            const mid =
                L.latLng(
                    (a.lat + b.lat) / 2,
                    (a.lng + b.lng) / 2
                );


            const label =
                L.marker(
                    mid,
                    {
                        icon:
                            L.divIcon(
                                {
                                    className:
                                        "measurement-label",

                                    html:
                                        `<span>
                                            ${this.formatDistance(
                                                segments[i - 1]
                                            )}
                                        </span>`,

                                    iconSize:
                                        null,

                                    iconAnchor:
                                        [0, 0]
                                }
                            ),

                        interactive: false
                    }
                ).addTo(this.map);


            this.segmentLabels.push(
                label
            );
        }
    }


    /* ========================================================
       AREA
       ======================================================== */

    updateArea() {

        if (
            this.points.length < 3
        ) {

            this.setStatus(
                `Area: ${this.points.length} point — minimal 3 point`
            );

            return;
        }


        const latlngs =
            this.points.map(
                p => [p.lat, p.lng]
            );


        if (!this.polygon) {

            this.polygon =
                L.polygon(
                    latlngs,
                    {
                        color: "#1565c0",

                        weight: 3,

                        fillColor: "#1565c0",

                        fillOpacity: 0.15,

                        interactive: false
                    }
                ).addTo(this.map);

        } else {

            this.polygon.setLatLngs(
                latlngs
            );
        }


        const utmPoints =
            this.points.map(
                p =>
                    this.latLngToUTM(p)
            );


        const area =
            this.calculateArea(
                utmPoints
            );


        const perimeter =
            this.calculatePerimeter(
                utmPoints
            );


        this.resultPanel.innerHTML = `

            <div class="measurement-title">
                Area
            </div>

            <div class="measurement-total">
                ${this.formatArea(area)}
            </div>

            <div class="measurement-detail">
                Perimeter:
                ${this.formatDistance(perimeter)}
            </div>

            <div class="measurement-detail">
                ${this.points.length} points
            </div>

        `;
    }


    /* ========================================================
       AREA CALCULATION
       ======================================================== */

    calculateArea(
        points
    ) {

        let sum = 0;


        for (
            let i = 0;
            i < points.length;
            i++
        ) {

            const j =
                (i + 1) %
                points.length;


            sum +=
                points[i].easting *
                points[j].northing;


            sum -=
                points[j].easting *
                points[i].northing;
        }


        return Math.abs(
            sum / 2
        );
    }


    /* ========================================================
       PERIMETER
       ======================================================== */

    calculatePerimeter(
        points
    ) {

        let total = 0;


        for (
            let i = 0;
            i < points.length;
            i++
        ) {

            const j =
                (i + 1) %
                points.length;


            const dx =
                points[j].easting -
                points[i].easting;


            const dy =
                points[j].northing -
                points[i].northing;


            total +=
                Math.sqrt(
                    dx * dx +
                    dy * dy
                );
        }


        return total;
    }


    /* ========================================================
       WGS84 -> UTM 50N
       ======================================================== */

    latLngToUTM(
        latlng
    ) {

        const lat =
            latlng.lat;

        const lon =
            latlng.lng;


        /*
         * WGS84
         *
         * UTM Zone 50N
         * Central Meridian = 117E
         */

        const a =
            6378137.0;

        const eccSquared =
            0.00669438;

        const k0 =
            0.9996;


        const latRad =
            lat *
            Math.PI /
            180;


        const lonRad =
            lon *
            Math.PI /
            180;


        const zone =
            50;


        const longOrigin =
            (zone - 1) * 6 -
            180 +
            3;


        const longOriginRad =
            longOrigin *
            Math.PI /
            180;


        const eccPrimeSquared =
            eccSquared /
            (1 - eccSquared);


        const N =
            a /
            Math.sqrt(
                1 -
                eccSquared *
                Math.sin(latRad) *
                Math.sin(latRad)
            );


        const T =
            Math.tan(latRad) *
            Math.tan(latRad);


        const C =
            eccPrimeSquared *
            Math.cos(latRad) *
            Math.cos(latRad);


        const A =
            Math.cos(latRad) *
            (
                lonRad -
                longOriginRad
            );


        const M =
            a *
            (
                (1 -
                    eccSquared / 4 -
                    3 *
                    eccSquared *
                    eccSquared /
                    64 -
                    5 *
                    eccSquared *
                    eccSquared *
                    eccSquared /
                    256
                ) *
                latRad

                -

                (
                    3 *
                    eccSquared / 8 +

                    3 *
                    eccSquared *
                    eccSquared /
                    32 +

                    45 *
                    eccSquared *
                    eccSquared *
                    eccSquared /
                    1024
                ) *
                Math.sin(
                    2 * latRad
                )

                +

                (
                    15 *
                    eccSquared *
                    eccSquared /
                    256 +

                    45 *
                    eccSquared *
                    eccSquared *
                    eccSquared /
                    1024
                ) *
                Math.sin(
                    4 * latRad
                )

                -

                (
                    35 *
                    eccSquared *
                    eccSquared *
                    eccSquared /
                    3072
                ) *
                Math.sin(
                    6 * latRad
                )
            );


        const easting =
            k0 *
            N *
            (
                A +

                (1 - T + C) *
                Math.pow(A, 3) /
                6 +

                (
                    5 -
                    18 * T +
                    T * T +
                    72 * C -
                    58 * eccPrimeSquared
                ) *
                Math.pow(A, 5) /
                120
            )

            +

            500000.0;


        const northing =
            k0 *
            (
                M +

                N *
                Math.tan(latRad) *
                (
                    A * A / 2 +

                    (
                        5 -
                        T +
                        9 * C +
                        4 * C * C
                    ) *
                    Math.pow(A, 4) /
                    24 +

                    (
                        61 -
                        58 * T +
                        T * T +
                        600 * C -
                        330 * eccPrimeSquared
                    ) *
                    Math.pow(A, 6) /
                    720
                )
            );


        return {
            easting,
            northing,
            zone
        };
    }


    /* ========================================================
       CLEAR
       ======================================================== */

    clear() {

        this.points = [];


        this.markers.forEach(
            marker =>
                marker.remove()
        );

        this.markers = [];


        this.segmentLabels.forEach(
            label =>
                label.remove()
        );

        this.segmentLabels = [];


        if (this.line) {

            this.line.remove();

            this.line = null;
        }


        if (this.polygon) {

            this.polygon.remove();

            this.polygon = null;
        }


        this.setStatus(
            "Pilih Mode Pengukuran"
        );
    }


    /* ========================================================
       FINISH
       ======================================================== */

    finish() {

        if (
            this.mode === "distance" &&
            this.points.length >= 2
        ) {

            this.updateDistance();
        }


        if (
            this.mode === "area" &&
            this.points.length >= 3
        ) {

            this.updateArea();
        }

        AppState.measurementActive = false;
        this.stopMode();
    }


    /* ========================================================
       LABEL
       ======================================================== */

    clearLabels() {

        this.segmentLabels.forEach(
            label =>
                label.remove()
        );

        this.segmentLabels = [];
    }


    /* ========================================================
       UI STATE
       ======================================================== */

    updateModeButtons() {

        const distanceBtn =
            document.getElementById(
                "btnMeasureDistance"
            );

        const areaBtn =
            document.getElementById(
                "btnMeasureArea"
            );


        distanceBtn?.classList.toggle(
            "active",
            this.mode === "distance"
        );


        areaBtn?.classList.toggle(
            "active",
            this.mode === "area"
        );
    }


    setStatus(
        text
    ) {

        if (!this.resultPanel) {
            return;
        }


        this.resultPanel.innerHTML = `

            <div class="measurement-status">
                ${text}
            </div>

        `;
    }


    /* ========================================================
       FORMAT
       ======================================================== */

    formatDistance(
        value
    ) {

        if (
            value < 1000
        ) {

            return `${value.toFixed(2)} m`;
        }


        return `${(
            value / 1000
        ).toFixed(3)} km`;
    }


    formatArea(
        value
    ) {

        if (
            value < 10000
        ) {

            return `${value.toFixed(2)} m²`;
        }


        return `${(
            value / 10000
        ).toFixed(4)} ha`;
    }
}


export default new Measurement();